import { isPlatformServer } from '@angular/common';
import { httpResource } from '@angular/common/http';
import type { Resource, Signal, StateKey } from '@angular/core';
import {
	computed,
	effect,
	inject,
	Injectable,
	makeStateKey,
	PLATFORM_ID,
	signal,
	TransferState,
	untracked,
} from '@angular/core';
import type { UrlTree } from '@angular/router';
import { PRIMARY_OUTLET, Router } from '@angular/router';

import type { Language } from '@valk-nx/core/lib/core';
import { LANGUAGE } from '@valk-nx/core/lib/core';
import { queryParamsMapEqual } from '@valk-nx/services/query-params';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type {
	FilterableHotelListData,
	FilterableListFilter,
	FilterableListItems,
	FilterableListLocation,
	FilterableListScenario,
	FilterablePackageBatchListData,
	FilterableRoomListData,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

import { withTransferredValue } from '../helpers/transfer-state-resource';

import {
	CONTENT_SERVICE_URL,
	CONTENT_WITH_CREDENTIALS,
} from './content.service';
import type { FiltersList } from './filterable-list.filters';
import {
	initialListItemKeys,
	initialListItemParams,
	isInitialParamKey,
	preselectedFilters,
	pruneFilters,
} from './filterable-list.filters';

export const DEFAULT_DATA: FilterableHotelListData = {
	hotels: [],
	filters: [],
	mapHotels: [],
};

export const DEFAULT_PACKAGES_DATA: FilterablePackageBatchListData = {
	packageBatches: [],
	filters: [],
	mapHotels: [],
};

export const DEFAULT_ROOMS_DATA: FilterableRoomListData = {
	rooms: [],
	filters: [],
	mapHotels: [],
};

type FilterParams = Record<string, string>;

function extractLanguageCode(
	urlTree: UrlTree,
	rootLanguage: Language,
): Language {
	const segments = urlTree.root.children[PRIMARY_OUTLET]?.segments ?? [];

	return StoryblokHelper.getStoryblokParameters(
		segments,
		urlTree.queryParams,
		rootLanguage,
	).language;
}

@Injectable({ providedIn: 'root' })
export class FilterableListService {
	private readonly serviceUrl = inject(CONTENT_SERVICE_URL, {
		optional: true,
	});
	private readonly withCredentials = inject<{ withCredentials: boolean }>(
		CONTENT_WITH_CREDENTIALS,
		{ optional: true },
	);
	private readonly router = inject(Router);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly transferState = inject(TransferState);
	private readonly rootLanguage = inject<Language>(LANGUAGE);
	readonly language = computed<Language>(() => {
		const finalUrl = this.router.lastSuccessfulNavigation()?.finalUrl;

		return finalUrl
			? extractLanguageCode(finalUrl, this.rootLanguage)
			: this.rootLanguage;
	});

	filters = signal<FiltersList>({});
	scenario = signal<FilterableListScenario | null>(null);
	listItems = signal<FilterableListItems>({});
	// Deliberately not part of `filters`: pruneFilters drops every key
	// without a matching BFF filter definition
	location = signal<FilterableListLocation | null>(null);

	private readonly initialParams = computed<FilterParams>(() => {
		const items = this.listItems();
		return initialListItemParams(items, initialListItemKeys(items));
	});

	private readonly vdcHotelsRequestPath = computed<string | undefined>(() => {
		if (this.scenario() !== 'vdc-hotels') return undefined;

		const path = this.listItems()?.valkBusiness
			? 'valkbusiness/hotels'
			: 'vdc/hotels';

		return this.buildRequestPath(path);
	});

	private readonly vdcvhiHotelsRequestPath = computed<string | undefined>(
		() =>
			this.scenario() === 'vdcvhi-hotels'
				? this.buildRequestPath('valkcom/hotels')
				: undefined,
	);

	private readonly vdcPackagesRequestPath = computed<string | undefined>(
		() =>
			this.scenario() === 'vdc-packages'
				? this.buildRequestPath('package-batches')
				: undefined,
	);

	// Without an initial room type or room facility the BFF has no data set
	// to start from, so there is nothing to request
	private readonly vdcRoomsRequestPath = computed<string | undefined>(() => {
		if (this.scenario() !== 'vdc-rooms') return undefined;
		const { initialRoomType, initialRoomFacilities } = this.initialParams();
		return initialRoomType || initialRoomFacilities
			? this.buildRequestPath('filterable-rooms')
			: undefined;
	});

	readonly vdcHotels: Resource<FilterableHotelListData> =
		this.createTransferStateResource(
			this.vdcHotelsRequestPath,
			DEFAULT_DATA,
		);

	readonly vdcvhiHotels: Resource<FilterableHotelListData> =
		this.createTransferStateResource(
			this.vdcvhiHotelsRequestPath,
			DEFAULT_DATA,
		);

	readonly vdcPackages: Resource<FilterablePackageBatchListData> =
		this.createTransferStateResource(
			this.vdcPackagesRequestPath,
			DEFAULT_PACKAGES_DATA,
		);

	readonly vdcRooms: Resource<FilterableRoomListData> =
		this.createTransferStateResource(
			this.vdcRoomsRequestPath,
			DEFAULT_ROOMS_DATA,
		);

	private readonly activeResource = computed<
		| Resource<FilterableHotelListData>
		| Resource<FilterablePackageBatchListData>
		| Resource<FilterableRoomListData>
		| undefined
	>(() => {
		const scenario = this.scenario();
		if (scenario === 'vdc-hotels') return this.vdcHotels;
		if (scenario === 'vdcvhi-hotels') return this.vdcvhiHotels;
		if (scenario === 'vdc-rooms') return this.vdcRooms;
		// Stryker disable next-line ConditionalExpression: equivalent mutant — for unmapped scenarios an (idle) resource and undefined are indistinguishable behind the resolved-status guard
		if (scenario === 'vdc-packages') return this.vdcPackages;
		return undefined;
	});

	/**
	 * Filter definitions of the active scenario, but only while its resource
	 * is `resolved` — `withTransferredValue` reports `loading` mid-request,
	 * so pruning never runs against a stale in-flight snapshot.
	 */
	private readonly resolvedFilterDefinitions = computed<
		FilterableListFilter[] | undefined
	>(() => {
		const resource = this.activeResource();
		if (resource?.status() !== 'resolved') return undefined;
		return resource.value().filters;
	});

	constructor() {
		// We can only validate filters from the URL after the response
		// arrives, because that response contains the filter definitions.
		// Skip the write when nothing was pruned, so we don't refetch.
		effect(() => {
			const definitions = this.resolvedFilterDefinitions();
			if (!definitions) return;
			untracked(() => {
				const current = this.filters();
				const pruned = pruneFilters(
					current,
					definitions,
					preselectedFilters(this.listItems()),
				);
				if (!queryParamsMapEqual(current, pruned)) {
					this.filters.set(pruned);
				}
			});
		});
	}

	/** Builds `<path>?<initial params>&<sorted filters>&<location>&language=<lang>`. */
	private buildRequestPath(path: string): string {
		const params: FilterParams = { ...this.initialParams() };

		const filters = this.filters();
		// Sort keys so the query string (and the cache key derived from it) is
		// deterministic regardless of the order filters were applied. Reserved
		// `initial*` names are dropped: the URL must not replace or inject the
		// editor's data set, and pruning only catches that after a response.
		const sortedFilterKeys = Object.keys(filters)
			.filter((key) => !isInitialParamKey(key))
			.sort((a, b) => a.localeCompare(b));
		for (const key of sortedFilterKeys) {
			params[key] = filters[key].join(',');
		}

		const location = this.location();
		if (location?.type === 'country') {
			params['locationSearchCountry'] = location.countryCode;
		} else if (location) {
			params['latitude'] = `${location.latitude}`;
			params['longitude'] = `${location.longitude}`;
		}

		params['language'] = this.language();

		return `${path}?${new URLSearchParams(params).toString()}`;
	}

	private createTransferStateResource<T>(
		requestPath: Signal<string | undefined>,
		defaultValue: T,
	): Resource<T> {
		const stateKey = computed<StateKey<T> | undefined>(() => {
			const path = requestPath();
			return path
				? makeStateKey<T>(`filterable-list-${path}`)
				: undefined;
		});

		const resource = httpResource<T>(
			() => {
				const path = requestPath();
				if (!path) return undefined;

				const key = stateKey();
				// Skip the request as long as the key is transferred; the
				// key is never removed, so the initial param-set never
				// refetches during the session (see withTransferredValue)
				if (key && this.transferState.hasKey(key)) return undefined;

				return {
					url: `${this.serviceUrl}${path}`,
					method: 'GET',
					transferCache: false,
					...this.withCredentials,
				};
			},
			{
				defaultValue,
				parse: (raw) => (raw as { data: T }).data,
			},
		);

		effect(() => {
			const key = stateKey();
			if (
				isPlatformServer(this.platformId) &&
				key &&
				resource.status() === 'resolved'
			) {
				this.transferState.set(key, resource.value());
			}
		});

		return withTransferredValue(resource, stateKey, this.transferState);
	}
}
